import React, { useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Wallet, RefreshCw, Copy, Eye, Trash2, Save, ShieldCheck, Users } from "lucide-react";
import { QuotePreview } from "@/components/QuotePreview";
import { BillingWorkflowPanel } from "@/components/BillingWorkflowPanel";
import { DocumentRelationsPanel } from "@/components/DocumentRelationsPanel";
import { CatalogPanel } from "@/components/CatalogPanel";
import { computeAmounts, fmtCurrency, fmtDateTime, positionLineNet, positionQuantity, taxHintForCountry, trackingUrlFor } from "@/lib/quote-helpers";
import { INVOICE_TYPES, docLabel, offerLabel } from "@/lib/constants";
import { resolveOrgCurrency } from "@/lib/currency-context";

export const EditorTab = ({
  quote, activeId, total, payCfg, company = {}, customers, packages, attachments,
  signature, steuer, namedTemplates, previewRef, COUNTRY_PRESETS,
  setTab, hasFeature, toggleKleinunternehmer,
  switchDokumentTyp, convertToInvoice, convertTo, update, fetchNextNumber,
  copyTracking, applyCustomer, addCustomPosition, addPaket, updatePosition,
  removePosition, toggleAttachment, applyNamedTemplate, saveNamedTemplate,
  deleteNamedTemplate, onReloadQuote, onOpenGeneratedInvoice,
}) => {
  // Manuelle Zeilen bleiben nur in diesem Dokument. Sie werden bewusst nicht
  // in die Katalog-/Vorlagenbibliothek geschrieben.
  const storedManualLines = Array.isArray(quote.manual_billing_lines)
    ? quote.manual_billing_lines
    : [];
  const visibleManualLines = storedManualLines.length
    ? storedManualLines
    : [{
        id: "manual-1",
        quantity: quote.anzahl_bewertungen ?? 1,
        unit_price: quote.preis_pro_bewertung ?? 0,
      }];
  const updateManualLine = (index, patch) => {
    const current = storedManualLines.length
      ? storedManualLines
      : visibleManualLines;
    update("manual_billing_lines", current.map((line, lineIndex) =>
      lineIndex === index ? { ...line, ...patch } : line,
    ));
  };
  const addManualLine = () => {
    const current = storedManualLines.length
      ? storedManualLines
      : visibleManualLines;
    update("manual_billing_lines", [
      ...current,
      { id: `manual-${Date.now()}-${current.length + 1}`, quantity: 1, unit_price: 0 },
    ]);
  };
  const removeManualLine = (index) => {
    if (storedManualLines.length <= 1) {
      update("manual_billing_lines", []);
      return;
    }
    update("manual_billing_lines", storedManualLines.filter((_, lineIndex) => lineIndex !== index));
  };

  return (
  <>
    {INVOICE_TYPES.includes(quote.dokument_typ) && activeId && !payCfg.stripe_enabled && !payCfg.paypal_enabled && (
      <div
        className="mb-4 flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3"
        data-testid="stripe-hint-banner"
      >
        <Wallet className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
        <div className="text-sm text-amber-900">
          <span className="font-semibold">Noch kein Zahlungslink auf dieser Rechnung?</span>{" "}
          Damit Ihre Kunden online bezahlen können, hinterlegen Sie Ihren eigenen Stripe- oder PayPal-Zugang.
          <button
            type="button"
            onClick={() => setTab("settings")}
            className="ml-1 font-semibold underline underline-offset-2 hover:text-amber-700"
            data-testid="stripe-hint-goto-settings"
          >
            Jetzt in den Einstellungen einrichten →
          </button>
        </div>
      </div>
    )}
    <div className="grid grid-cols-1 xl:grid-cols-[420px_1fr] gap-6">
      <Card className="p-5 bg-white border-[var(--brand-border)]">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-4">
          <h2 className="font-[Playfair_Display] text-lg font-semibold text-[var(--brand-ink)]">
            {quote.dokument_typ === "rechnung" ? "Rechnungsdaten" : `${offerLabel(company)}daten`}
          </h2>
          <div className="flex bg-[var(--brand-border-light)] rounded-full p-1 text-xs shrink-0">
            <button
              type="button"
              onClick={() => switchDokumentTyp("angebot")}
              className={`px-3 py-1 rounded-full transition ${
                quote.dokument_typ !== "rechnung"
                  ? "bg-white text-[var(--brand-ink)] shadow-sm"
                  : "text-[var(--brand-muted)]"
              }`}
              data-testid="toggle-angebot"
            >
              {offerLabel(company)}
            </button>
            <button
              type="button"
              onClick={() => switchDokumentTyp("rechnung")}
              className={`px-3 py-1 rounded-full transition ${
                quote.dokument_typ === "rechnung"
                  ? "bg-[var(--brand-ink)] text-white shadow-sm"
                  : "text-[var(--brand-muted)]"
              }`}
              data-testid="toggle-rechnung"
            >
              Rechnung
            </button>
          </div>
        </div>

        {activeId && (
          <div className="mb-4 space-y-2" data-testid="convert-actions">
            <div className="text-xs uppercase tracking-wider text-[var(--brand-primary)] font-semibold">
              Dokument umwandeln
            </div>
            <div className="grid grid-cols-2 gap-2">
              {quote.dokument_typ === "angebot" && (
                <>
                  <Button variant="outline" size="sm" onClick={convertToInvoice}
                    className="border-[var(--brand-ink)]" data-testid="btn-convert-invoice">
                    → Rechnung
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => convertTo("to-anzahlungsrechnung", "Anzahlungsrechnung")}
                    className="border-[var(--brand-border)]" data-testid="btn-convert-anzahlung">
                    → Anzahlungsrechnung
                  </Button>
                </>
              )}
              {(quote.dokument_typ === "angebot" || quote.dokument_typ === "anzahlungsrechnung") && (
                <Button variant="outline" size="sm" onClick={() => convertTo("to-schlussrechnung", "Schlussrechnung")}
                  className="border-[var(--brand-border)]" data-testid="btn-convert-schluss">
                  → Schlussrechnung
                </Button>
              )}
              {INVOICE_TYPES.includes(quote.dokument_typ) && (
                <>
                  <Button variant="outline" size="sm" onClick={() => convertTo("to-gutschrift", "Gutschrift")}
                    className="border-[var(--brand-border)]" data-testid="btn-convert-gutschrift">
                    → Gutschrift
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => convertTo("to-storno", "Stornorechnung")}
                    className="border-[var(--brand-border)]" data-testid="btn-convert-storno">
                    → Stornorechnung
                  </Button>
                </>
              )}
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>
                {quote.dokument_typ === "rechnung"
                  ? "Rechnungsnummer"
                  : `${offerLabel(company)}nummer`}
              </Label>
              <div className="flex gap-1">
                <Input
                  value={quote.angebot_nr}
                  onChange={(e) => update("angebot_nr", e.target.value)}
                  data-testid="input-angebot-nr"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => fetchNextNumber()}
                  title="Nächste Nummer"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div>
              <Label>Datum</Label>
              <Input
                type="date"
                value={quote.angebot_datum}
                onChange={(e) => update("angebot_datum", e.target.value)}
                data-testid="input-datum"
              />
            </div>
          </div>

          <div>
            <Label>
              {quote.dokument_typ === "rechnung"
                ? "Fällig bis"
                : "Gültig bis"}
            </Label>
            <Input
              type="date"
              value={quote.gueltig_bis}
              onChange={(e) => update("gueltig_bis", e.target.value)}
              data-testid="input-gueltig-bis"
            />
          </div>

          <div>
            <Label>Leistungsdatum / -zeitraum</Label>
            <Input
              value={quote.leistungsdatum}
              onChange={(e) => update("leistungsdatum", e.target.value)}
              placeholder="z. B. Nach Auftragserteilung"
              data-testid="input-leistungsdatum"
            />
          </div>

          {activeId && quote.angebot_nr && (
            <div className="rounded border border-[var(--brand-border)] bg-[var(--brand-bg)] p-3">
              <div className="text-[11px] uppercase tracking-wider text-[var(--brand-primary)] font-semibold mb-1">
                Tracking-Link für den Kunden
              </div>
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={trackingUrlFor(quote.public_token)}
                  className="font-mono text-xs"
                  data-testid="tracking-url"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={copyTracking}
                  data-testid="copy-tracking"
                  title="Link kopieren"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <div className="text-xs text-[var(--brand-muted)] mt-2 flex items-center gap-3">
                <span className="flex items-center gap-1">
                  <Eye className="w-3 h-3" /> {quote.views || 0} Aufruf
                  {(quote.views || 0) === 1 ? "" : "e"}
                </span>
                {quote.last_viewed_at && (
                  <span>zuletzt {fmtDateTime(quote.last_viewed_at)}</span>
                )}
              </div>
            </div>
          )}

          <Separator />

          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm uppercase tracking-wider text-[var(--brand-primary)]">
              Kunde
            </h3>
            <div className="relative">
              <Button
                variant="outline"
                size="sm"
                className="text-xs gap-1"
                onClick={() => {
                  const el = document.getElementById('customer-picker-dropdown');
                  if (el) el.classList.toggle('hidden');
                }}
              >
                <Users className="w-3 h-3" /> Kunde waehlen
              </Button>
              <div id="customer-picker-dropdown" className="hidden absolute right-0 top-full mt-1 z-50 w-72 max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg p-2">
                <input
                  type="text"
                  placeholder="Suchen..."
                  className="w-full px-2 py-1 mb-2 text-sm border rounded"
                  onChange={(e) => {
                    const q = e.target.value.toLowerCase();
                    document.querySelectorAll('[data-customer-item]').forEach(el => {
                      el.style.display = el.textContent.toLowerCase().includes(q) ? '' : 'none';
                    });
                  }}
                />
                {customers.length === 0 && <p className="text-xs text-gray-400 p-2">Keine Kunden vorhanden</p>}
                {customers.map((c) => (
                  <button
                    key={c.id}
                    data-customer-item
                    className="w-full text-left px-2 py-1.5 text-sm hover:bg-gray-100 rounded flex flex-col"
                    onClick={() => {
                      applyCustomer(c);
                      document.getElementById('customer-picker-dropdown').classList.add('hidden');
                    }}
                  >
                    <span className="font-medium">{c.kunde_name || 'Unbenannt'}</span>
                    {c.kunde_firma && <span className="text-xs text-gray-500">{c.kunde_firma}</span>}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-[120px_1fr] gap-3">
            <div>
              <Label>Anrede</Label>
              <Select
                value={quote.anrede}
                onValueChange={(v) => update("anrede", v)}
              >
                <SelectTrigger data-testid="select-anrede">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Frau">Frau</SelectItem>
                  <SelectItem value="Herr">Herr</SelectItem>
                  <SelectItem value="Divers">Divers</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Name</Label>
              <Input
                value={quote.kunde_name}
                list="customer-list"
                onChange={(e) => {
                  const v = e.target.value;
                  const match = customers.find(
                    (c) => (c.kunde_name || "") === v
                  );
                  if (match) {
                    applyCustomer(match);
                  } else {
                    update("kunde_name", v);
                  }
                }}
                data-testid="input-kunde-name"
              />
              <datalist id="customer-list">
                {customers.map((c) => (
                  <option key={c.id} value={c.kunde_name}>
                    {c.kunde_firma}
                  </option>
                ))}
              </datalist>
            </div>
          </div>

          <div>
            <Label>Firma</Label>
            <Input
              value={quote.kunde_firma}
              onChange={(e) => update("kunde_firma", e.target.value)}
              data-testid="input-kunde-firma"
            />
          </div>
          <div>
            <Label>Straße und Hausnummer</Label>
            <Input
              value={quote.kunde_strasse}
              onChange={(e) => update("kunde_strasse", e.target.value)}
              data-testid="input-kunde-strasse"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>PLZ und Ort</Label>
              <Input
                value={quote.kunde_plz_ort}
                onChange={(e) => update("kunde_plz_ort", e.target.value)}
                data-testid="input-kunde-plz-ort"
              />
            </div>
            <div>
              <Label>Land</Label>
              <Select
                value={quote.kunde_land}
                onValueChange={(v) => update("kunde_land", v)}
              >
                <SelectTrigger data-testid="select-kunde-land">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Deutschland">Deutschland</SelectItem>
                  <SelectItem value="Österreich">Österreich</SelectItem>
                  <SelectItem value="Schweiz">Schweiz</SelectItem>
                  <SelectItem value="Luxemburg">Luxemburg</SelectItem>
                  <SelectItem value="Belgien">Belgien</SelectItem>
                  <SelectItem value="Niederlande">Niederlande</SelectItem>
                  <SelectItem value="Frankreich">Frankreich</SelectItem>
                  <SelectItem value="Italien">Italien</SelectItem>
                  <SelectItem value="Spanien">Spanien</SelectItem>
                  <SelectItem value="Polen">Polen</SelectItem>
                  <SelectItem value="Tschechien">Tschechien</SelectItem>
                  <SelectItem value="Liechtenstein">Liechtenstein</SelectItem>
                  <SelectItem value="Großbritannien">Großbritannien</SelectItem>
                  <SelectItem value="USA">USA</SelectItem>
                  <SelectItem value="Sonstiges">Sonstiges</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>E-Mail</Label>
              <Input
                value={quote.kunde_email}
                onChange={(e) => update("kunde_email", e.target.value)}
                data-testid="input-kunde-email"
              />
            </div>
            <div>
              <Label>Telefon</Label>
              <Input
                value={quote.kunde_telefon}
                onChange={(e) => update("kunde_telefon", e.target.value)}
                data-testid="input-kunde-telefon"
              />
            </div>
          </div>

          <div>
            <Label>USt-IdNr. des Kunden (bei B2B Ausland)</Label>
            <Input
              value={quote.kunde_ustid}
              onChange={(e) => update("kunde_ustid", e.target.value)}
              placeholder="z. B. ATU12345678"
              data-testid="input-kunde-ustid"
            />
          </div>
          <div className="rounded-md border border-[var(--brand-border)] bg-[var(--brand-bg)] p-3 space-y-3">
            <div>
              <p className="text-sm font-medium text-[var(--brand-ink)]">XRechnung-Empfangsdaten (nur falls gefordert)</p>
              <p className="mt-1 text-xs text-[var(--brand-muted)]">Die normale E-Mail-Adresse ist kein XRechnung-Endpunkt. Für Behörden ist meist die Leitweg-ID mit EAS-Code 0204 erforderlich.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2"><Label>Elektronische Adresse / Leitweg-ID</Label><Input value={quote.kunde_einvoice_endpoint || ""} onChange={(e) => update("kunde_einvoice_endpoint", e.target.value)} placeholder="z. B. 04011000-12345-67" data-testid="input-kunde-einvoice-endpoint" /></div>
              <div><Label>CEF-EAS-Code</Label><Input value={quote.kunde_einvoice_endpoint_scheme || ""} onChange={(e) => update("kunde_einvoice_endpoint_scheme", e.target.value)} placeholder="0204" inputMode="numeric" data-testid="input-kunde-einvoice-scheme" /></div>
            </div>
            <div><Label>Käuferreferenz</Label><Input value={quote.kunde_buyer_reference || ""} onChange={(e) => update("kunde_buyer_reference", e.target.value)} placeholder="z. B. Leitweg-ID" data-testid="input-kunde-buyer-reference" /></div>
          </div>

          {quote.kunde_land &&
            (quote.kunde_land.toLowerCase() !== "deutschland" || String(company?.main_country || company?.land || "").toUpperCase() === "CH") && (
              <div
                className="rounded border-l-4 p-3 text-xs"
                style={{
                  background: "var(--brand-bg)",
                  borderLeftColor: "var(--brand-primary)",
                }}
                data-testid="tax-hint-banner"
              >
                <div className="font-semibold text-[var(--brand-ink)] mb-1">
                  {taxHintForCountry(quote.kunde_land, company, quote.currency).kind === "swiss" ? "Hinweis Schweiz" : "Hinweis Auslandskunde"}: {taxHintForCountry(quote.kunde_land, company, quote.currency).short}
                </div>
                <div className="text-[#5b5b5b]">
                  {taxHintForCountry(quote.kunde_land, company, quote.currency).long}
                </div>
              </div>
            )}

          <Separator />

          <h3 className="font-semibold text-sm uppercase tracking-wider text-[var(--brand-primary)]">
            Leistung
          </h3>
          <div>
            <Label>Leistungs-Titel</Label>
            <Input
              value={quote.leistung_titel}
              onChange={(e) => update("leistung_titel", e.target.value)}
              data-testid="input-leistung-titel"
            />
          </div>
          <div>
            <Label>Beschreibung</Label>
            <Textarea
              rows={3}
              value={quote.leistung_beschreibung}
              onChange={(e) =>
                update("leistung_beschreibung", e.target.value)
              }
              data-testid="input-leistung-beschreibung"
            />
          </div>

          <div
            className="rounded-md border border-[var(--brand-border)] bg-[var(--brand-bg)] p-3 space-y-2"
            data-testid="manual-billing-lines"
          >
            <div className="flex items-center justify-between gap-2">
              <Label className="text-sm font-semibold text-[var(--brand-ink)]">
                Manuelle Abrechnung
              </Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={addManualLine}
                data-testid="btn-add-manual-line"
              >
                + Weitere Zeile
              </Button>
            </div>
            <p className="text-xs text-[var(--brand-muted)]">
              Nur für dieses Dokument – wird nicht als Leistung gespeichert.
            </p>
            {visibleManualLines.map((line, index) => (
              <div
                key={line.id || index}
                className="grid grid-cols-2 sm:grid-cols-[3.5rem_1.25fr_0.8fr_1fr_1fr] gap-2 items-end"
                data-testid={`manual-line-${index}`}
              >
                <div>
                  <Label className="text-xs">Nr.</Label>
                  <div className="h-10 rounded border border-[var(--brand-border)] bg-white px-3 flex items-center text-sm font-semibold">
                    {index + 1}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Leistung / Position</Label>
                  <Input
                    value={line.title ?? ""}
                    onChange={(e) => updateManualLine(index, { title: e.target.value })}
                    placeholder={index === 0 ? "wie Leistungs-Titel oben" : `z. B. Position ${index + 1}`}
                    data-testid={`manual-line-title-${index}`}
                  />
                </div>
                <div>
                  <Label className="text-xs">Menge</Label>
                  <Input
                    type="number"
                    min="0.001"
                    step="0.001"
                    value={line.quantity ?? 1}
                    onChange={(e) => updateManualLine(index, { quantity: e.target.value === "" ? "" : Number(e.target.value) })}
                    data-testid={`manual-line-quantity-${index}`}
                  />
                </div>
                <div>
                  <Label className="text-xs">Einzelpreis netto ({quote.currency || resolveOrgCurrency(company)})</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={line.unit_price ?? 0}
                    onChange={(e) => updateManualLine(index, { unit_price: e.target.value === "" ? "" : Number(e.target.value) })}
                    data-testid={`manual-line-unit-price-${index}`}
                  />
                </div>
                <div>
                  <Label className="text-xs">Gesamt</Label>
                  <div className="h-10 rounded border border-[var(--brand-border)] bg-white px-3 flex items-center justify-between gap-1 text-sm font-semibold" data-testid={`manual-line-total-${index}`}>
                    <span>{fmtCurrency(Number(line.quantity || 0) * Number(line.unit_price || 0), quote.currency || resolveOrgCurrency(company))}</span>
                    {visibleManualLines.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeManualLine(index)}
                        className="text-[var(--brand-primary)] hover:opacity-70"
                        title="Manuelle Zeile entfernen"
                        aria-label={`Manuelle Zeile ${index + 1} entfernen`}
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div>
            <Label>Anschreiben / Einleitung (erscheint im Angebot nach &bdquo;Sehr geehrte/r &hellip;&ldquo;)</Label>
            <Textarea
              rows={6}
              value={quote.einleitung || ""}
              onChange={(e) => update("einleitung", e.target.value)}
              placeholder="vielen Dank für Ihre Anfrage…"
              data-testid="input-einleitung"
            />
          </div>

          {/* Paket-Bibliothek (Social Media + Google Ads) */}
          <div
            className="rounded-md border border-[var(--brand-border)] bg-[var(--brand-bg)] p-3 space-y-3"
            data-testid="paket-bibliothek"
          >
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold text-[var(--brand-muted)]">
                Leistungen / Positionen
              </Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={addCustomPosition}
                data-testid="btn-add-custom-position"
              >
                + Eigene Position
              </Button>
            </div>
            <p className="text-xs text-[var(--brand-muted)]">
              Jede Position hat eigene Felder für <strong>Menge, Einzelpreis und Gesamt</strong>.
              Das Häkchen <strong>„In Gesamtbetrag“</strong> bestimmt, welche Positionen in die Summe einfließen.
            </p>
            <div className="grid grid-cols-2 gap-2">
              {packages.length === 0 && (
                <p className="col-span-2 text-xs text-[var(--brand-muted)] italic">
                  Noch keine Pakete. Lege eigene Pakete unter „Einstellungen → Pakete“ an.
                </p>
              )}
              {packages.map((p) => (
                <Button
                  key={p.id}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="justify-start text-left h-auto py-2"
                  onClick={() => {
                    addPaket(p);
                    toast.success(`"${p.titel}" hinzugefügt`);
                  }}
                  data-testid={`btn-add-paket-${p.id}`}
                >
                  <div className="flex flex-col items-start">
                    <span className="font-medium leading-tight">
                      {p.titel}
                    </span>
                    <span className="text-xs text-[var(--brand-muted)] mt-0.5">
                      {fmtCurrency(p.preis)} {p.einheit}
                    </span>
                  </div>
                </Button>
              ))}
            </div>

            {(quote.positions || []).length > 0 && (
              <div className="space-y-2 pt-2 border-t border-[var(--brand-border)]">
                <Label className="text-xs uppercase tracking-wider text-[var(--brand-primary)]">
                  Positionen für dieses Dokument
                </Label>
                {(quote.positions || []).map((pos, idx) => (
                  <div
                    key={pos.id || idx}
                    className="bg-white rounded border border-[var(--brand-border)] p-3 space-y-2"
                    data-testid={`position-${idx}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 space-y-1.5">
                        <Label className="text-xs">Leistung / Position</Label>
                        <Input
                          value={pos.titel}
                          onChange={(e) =>
                            updatePosition(idx, {
                              titel: e.target.value,
                            })
                          }
                          className="font-medium"
                          data-testid={`pos-titel-${idx}`}
                        />
                        <label
                          className="flex items-center gap-2 cursor-pointer select-none whitespace-nowrap text-xs"
                          data-testid={`pos-include-${idx}`}
                          title="Wenn aktiv, wird der Preis in den Gesamtbetrag eingerechnet"
                        >
                          <input
                            type="checkbox"
                            className="w-4 h-4 accent-[var(--brand-primary)]"
                            checked={!!pos.include_in_total}
                            onChange={(e) =>
                              updatePosition(idx, {
                                include_in_total: e.target.checked,
                              })
                            }
                          />
                          <span
                            className={
                              pos.include_in_total
                                ? "text-[var(--brand-primary)] font-semibold"
                                : "text-[var(--brand-muted)]"
                            }
                          >
                            In Gesamtbetrag
                          </span>
                        </label>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removePosition(idx)}
                        data-testid={`btn-remove-pos-${idx}`}
                        className="text-[var(--brand-primary)] hover:bg-[var(--brand-bg)]"
                        title="Position entfernen"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <div>
                      <Label className="text-xs">Beschreibung / Details der Position</Label>
                      <Textarea
                        rows={4}
                        value={(pos.bullets || []).join("\n")}
                        onChange={(e) =>
                          updatePosition(idx, {
                            bullets: e.target.value
                              .split("\n")
                              .map((s) => s.trimEnd()),
                          })
                        }
                        placeholder="Details dieser Position – eine Zeile pro Punkt"
                        className="mt-1 text-sm"
                        data-testid={`pos-bullets-${idx}`}
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2">
                      <div>
                        <Label className="text-xs">Menge / Anzahl</Label>
                        <Input type="number" min="0.001" step="0.001" value={pos.quantity ?? 1} onChange={(e) => updatePosition(idx, { quantity: e.target.value === "" ? "" : Number(e.target.value) })} placeholder="z. B. 572" data-testid={`pos-quantity-${idx}`} />
                      </div>
                      <div>
                        <Label className="text-xs">Einheit</Label>
                        <Input value={pos.einheit || ""} onChange={(e) => updatePosition(idx, { einheit: e.target.value })} placeholder="z. B. Stunden" data-testid={`pos-einheit-${idx}`} />
                      </div>
                      <div><Label className="text-xs">Einzelpreis netto ({quote.currency || resolveOrgCurrency(company)})</Label><Input type="number" min="0" step="0.01" value={pos.preis} onChange={(e) => updatePosition(idx, { preis: Number(e.target.value) })} data-testid={`pos-preis-${idx}`} /></div>
                      <div className="rounded border border-[var(--brand-border)] bg-[var(--brand-bg)] px-3 py-2"><Label className="text-xs">Gesamt netto</Label><div className="mt-1 text-sm font-semibold text-[var(--brand-ink)]" data-testid={`pos-total-${idx}`}>{fmtCurrency(positionLineNet(pos), quote.currency || resolveOrgCurrency(company))}</div>{!pos.include_in_total && <div className="mt-0.5 text-[10px] text-[var(--brand-muted)]">nicht eingerechnet</div>}</div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div><Label className="text-xs">Steuer %</Label><Input type="number" min="0" step="0.1" value={pos.tax_rate ?? quote.tax_rate ?? 0} onChange={(e) => updatePosition(idx, { tax_rate: Number(e.target.value) })} disabled={steuer?.tax_status === "kleinunternehmer" || quote.tax_profile === "reverse_charge"} /></div>
                      <div><Label className="text-xs">Positionsrabatt</Label><div className="flex gap-1"><select className="w-16 border rounded text-xs" value={pos.discount_type || ""} onChange={(e) => updatePosition(idx, { discount_type: e.target.value })}><option value="">–</option><option value="percent">%</option><option value="amount">{quote.currency || resolveOrgCurrency(company)}</option></select><Input type="number" min="0" step="0.01" value={pos.discount_value || ""} onChange={(e) => updatePosition(idx, { discount_value: Number(e.target.value) })} /></div></div>
                    </div>
                    {/^\d+(?:[.,]\d+)?$/.test(String(pos.einheit || "").trim()) && (
                      <p className="rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-900" data-testid={`pos-unit-number-warning-${idx}`}>
                        Die Einheit ist nur eine Bezeichnung, zum Beispiel „Stunden“. Bitte die Zahl <strong>{pos.einheit}</strong> links bei <strong>„Menge / Anzahl“</strong> eintragen.
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

                    <CatalogPanel quote={quote} onUpdate={update} compact />

          <Separator />

          <h3 className="font-semibold text-sm uppercase tracking-wider text-[var(--brand-primary)]">
            Layout & Vorlagen

          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>PDF-Layout</Label>
              <select
                value={quote.layout || "classic"}
                onChange={(e) => update("layout", e.target.value)}
                className="w-full border border-[var(--brand-border)] rounded px-2 py-2 text-sm bg-white"
                data-testid="select-layout"
              >
                <option value="classic">Klassisch</option>
                <option value="modern" disabled={!hasFeature("templates")}>Modern</option>
                <option value="minimal" disabled={!hasFeature("templates")}>Minimal</option>
              </select>
              {!hasFeature("templates") && (
                <p className="text-[11px] text-amber-600 mt-1" data-testid="lock-templates">🔒 Weitere Layouts ab Pro</p>
              )}
            </div>
            <div>
              <Label>Vorlage laden</Label>
              <select
                value=""
                onChange={(e) => {
                  const t = namedTemplates.find((x) => x.id === e.target.value);
                  if (t) applyNamedTemplate(t);
                  e.target.value = "";
                }}
                className="w-full border border-[var(--brand-border)] rounded px-2 py-2 text-sm bg-white"
                data-testid="select-named-template"
              >
                <option value="">– Vorlage wählen –</option>
                {namedTemplates.map((t) => (
                  <option key={t.id} value={t.id}>{`${t.name} (${docLabel(t.typ, company)})`}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={saveNamedTemplate}
              className="border-[var(--brand-border)]" data-testid="save-named-template-btn">
              <Save className="w-4 h-4 mr-1" /> Als Vorlage speichern
            </Button>
            {namedTemplates.length > 0 && (
              <div className="flex items-center gap-1 flex-wrap text-xs">
                {namedTemplates.map((t) => (
                  <span key={t.id} className="inline-flex items-center gap-1 bg-[var(--brand-border-light)] rounded-full px-2 py-1">
                    {t.name}
                    <button type="button" onClick={() => deleteNamedTemplate(t.id)}
                      className="text-red-500" data-testid={`del-tpl-${t.id}`} title="Löschen">×</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <Separator />
          <h3 className="font-semibold text-sm uppercase tracking-wider text-[var(--brand-primary)]">
            Land, Steuer & Anzahlung
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Land</Label>
              <select
                value={quote.country || "DE"}
                onChange={(e) => {
                  const c = e.target.value;
                  const preset = COUNTRY_PRESETS[c];
                  update("country", c);
                  update("currency", preset.currency);
                  update("tax_rate", steuer?.tax_status === "kleinunternehmer" ? 0 : preset.rates[0]);
                }}
                className="w-full border border-[var(--brand-border)] rounded px-2 py-2 text-sm bg-white"
                data-testid="select-country"
              >
                {Object.entries(COUNTRY_PRESETS).map(([k, v]) => (
                  <option key={k} value={k} disabled={k !== "DE" && !hasFeature("dach")}>{v.label}</option>
                ))}
              </select>
              {!hasFeature("dach") && (
                <p className="text-[11px] text-amber-600 mt-1" data-testid="lock-dach">🔒 AT/CH & weitere Länder ab Pro</p>
              )}
            </div>
            <div>
              <Label>Währung</Label>
              <select
                value={quote.currency || "EUR"}
                onChange={(e) => update("currency", e.target.value)}
                className="w-full border border-[var(--brand-border)] rounded px-2 py-2 text-sm bg-white"
                data-testid="select-currency"
              >
                <option value="EUR">EUR (€)</option>
                <option value="CHF" disabled={!hasFeature("dach")}>CHF</option>
              </select>
            </div>
          </div>
          {(quote.country || "DE") === "DE" && (
            <label
              className="flex items-start gap-2 rounded-lg border border-[var(--brand-border)] bg-[var(--brand-bg)] px-3 py-2.5 cursor-pointer"
              data-testid="editor-ku-toggle"
            >
              <input
                type="checkbox"
                checked={steuer?.tax_status === "kleinunternehmer"}
                onChange={(e) => toggleKleinunternehmer?.(e.target.checked)}
                className="mt-0.5 accent-[var(--brand-primary)]"
                data-testid="editor-ku-checkbox"
              />
              <span className="text-sm">
                <span className="font-medium text-[var(--brand-ink)]">Kleinunternehmer (§ 19 UStG) – von der Umsatzsteuer befreit</span>
                <span className="block text-xs text-[var(--brand-muted)]">
                  Gilt für alle Dokumente: MwSt 0 %, Hinweis „Gemäß § 19 UStG wird keine Umsatzsteuer berechnet." erscheint automatisch.
                </span>
              </span>
            </label>
          )}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Standard-MwSt %</Label>
              <Input
                type="number" min="0" step="0.1"
                value={quote.tax_rate}
                onChange={(e) => update("tax_rate", Number(e.target.value))}
                disabled={steuer?.tax_status === "kleinunternehmer"}
                data-testid="input-tax-rate"
              />
              {steuer?.tax_status === "kleinunternehmer" && (
                <p className="text-[11px] text-[var(--brand-muted)] mt-1" data-testid="ku-editor-hint">
                  § 19 UStG aktiv – keine USt
                </p>
              )}
            </div>
            <div>
              <Label>Anzahlung</Label>
              <select
                value={quote.deposit_type || "percent"}
                onChange={(e) => update("deposit_type", e.target.value)}
                className="w-full border border-[var(--brand-border)] rounded px-2 py-2 text-sm bg-white"
                data-testid="select-deposit-type"
              >
                <option value="percent">in %</option>
                <option value="amount">Betrag</option>
              </select>
            </div>
            <div>
              <Label>{quote.deposit_type === "amount" ? "Betrag" : "Prozent"}</Label>
              <Input
                type="number" min="0" step="0.01"
                value={quote.deposit_value}
                onChange={(e) => update("deposit_value", Number(e.target.value))}
                placeholder={quote.deposit_type === "amount" ? "z. B. 500" : "z. B. 30"}
                data-testid="input-deposit-value"
                disabled={!hasFeature("deposits")}
              />
            </div>
          </div>
          {!hasFeature("deposits") && (
            <p className="text-[11px] text-amber-600 -mt-2" data-testid="lock-deposits">🔒 Anzahlungen & Schlussrechnungen ab Pro</p>
          )}

          <div className="grid grid-cols-2 gap-3 mt-3"><div><Label>Dokumentrabatt</Label><div className="flex gap-1"><select className="w-20 border rounded px-1 text-sm" value={quote.document_discount_type || ""} onChange={(e) => update("document_discount_type", e.target.value)}><option value="">–</option><option value="percent">%</option><option value="amount">{quote.currency || resolveOrgCurrency(company)}</option></select><Input type="number" min="0" step="0.01" value={quote.document_discount_value || ""} onChange={(e) => update("document_discount_value", Number(e.target.value))} /></div></div><div><Label>Steuerbehandlung</Label><select className="w-full border rounded px-2 py-2 text-sm" value={quote.tax_profile || (steuer?.tax_status === "kleinunternehmer" ? "kleinunternehmer" : "standard")} onChange={(e) => { update("tax_profile", e.target.value); update("reverse_charge", e.target.value === "reverse_charge"); }}><option value="standard">Regelbesteuerung</option><option value="reverse_charge">Reverse Charge</option><option value="kleinunternehmer">Kleinunternehmer</option></select></div></div>
          {quote.tax_profile === "reverse_charge" && <p className="text-xs text-amber-700 mt-2">Reverse Charge aktiv: Bitte Voraussetzungen, Leistungsort und Hinweistext mit der Steuerberatung prüfen. Umsatzsteuer wird im Dokument nicht ausgewiesen.</p>}
          <div className="rounded bg-[var(--brand-border-light)] px-3 py-2 space-y-1" data-testid="amount-summary">
            {(() => {
              const a = computeAmounts(quote);
              return (
                <>
                  <div className="flex justify-between text-sm">
                    <span>Netto</span><span>{fmtCurrency(a.net, a.currency)}</span>
                  </div>
                  {a.document_discount > 0 && <div className="flex justify-between text-sm text-[var(--brand-primary)]"><span>Dokumentrabatt</span><span>− {fmtCurrency(a.document_discount, a.currency)}</span></div>}
                  {(a.tax_groups || []).map((group) => group.tax > 0 && <div key={group.rate} className="flex justify-between text-sm text-[var(--brand-muted)]"><span>MwSt {group.effective_rate}% auf {fmtCurrency(group.net, a.currency)}</span><span>{fmtCurrency(group.tax, a.currency)}</span></div>)}
                  {a.reverse_charge && <div className="text-xs text-amber-700">Reverse Charge: Steuerschuldnerschaft des Leistungsempfängers</div>}
                  {a.kleinunternehmer && <div className="text-xs text-[var(--brand-muted)]">Kleinunternehmer: keine Umsatzsteuer ausgewiesen</div>}
                  <div className="flex justify-between items-center pt-1 border-t border-[var(--brand-border)]">
                    <span className="text-sm font-semibold">Gesamt</span>
                    <span className="font-[Playfair_Display] font-bold text-xl text-[var(--brand-ink)]" data-testid="total-amount">
                      {fmtCurrency(a.gross, a.currency)}
                    </span>
                  </div>
                  {a.deposit > 0 && (
                    <>
                      <div className="flex justify-between text-sm text-[var(--brand-primary)] font-medium" data-testid="deposit-amount">
                        <span>Anzahlung</span><span>{fmtCurrency(a.deposit, a.currency)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Restbetrag</span><span>{fmtCurrency(a.rest, a.currency)}</span>
                      </div>
                    </>
                  )}
                  {a.prepaid > 0 && (
                    <>
                      <div className="flex justify-between text-sm text-[var(--brand-muted)]" data-testid="prepaid-amount">
                        <span>abzgl. Anzahlung</span><span>− {fmtCurrency(a.prepaid, a.currency)}</span>
                      </div>
                      <div className="flex justify-between text-sm font-semibold text-[var(--brand-primary)]" data-testid="due-amount">
                        <span>Noch zu zahlen</span><span>{fmtCurrency(a.due, a.currency)}</span>
                      </div>
                    </>
                  )}
                </>
              );
            })()}
          </div>

          <Separator />

          <h3 className="font-semibold text-sm uppercase tracking-wider text-[var(--brand-primary)]">
            Zahlung
          </h3>
          <div>
            <Label>Zahlungsziel</Label>
            <Input
              value={quote.zahlungsziel}
              onChange={(e) => update("zahlungsziel", e.target.value)}
              data-testid="input-zahlungsziel"
            />
          </div>
          <div className="grid grid-cols-2 gap-3"><div><Label>Skonto %</Label><Input type="number" min="0" step="0.1" value={quote.skonto_percent || ""} onChange={(e) => update("skonto_percent", Number(e.target.value))} placeholder="z. B. 2" /></div><div><Label>Skontofrist (Tage)</Label><Input type="number" min="0" step="1" value={quote.skonto_days || ""} onChange={(e) => update("skonto_days", Number(e.target.value))} placeholder="z. B. 10" /></div></div>
          {Number(quote.skonto_percent || 0) > 0 && <p className="text-xs text-[var(--brand-muted)]">Skonto wird beim bestätigten Zahlungsabgleich geprüft. Der voreilige Abzug bleibt bis zur Prüfung als Restbetrag sichtbar.</p>}
          <div>
            <Label>Zahlungslink (optional)</Label>
            <Input
              value={quote.reseller_link}
              onChange={(e) => update("reseller_link", e.target.value)}
              placeholder="https://..."
              data-testid="input-link"
            />
          </div>
          <div>
            <Label>Notizen / Zusatztext (optional)</Label>
            <Textarea
              rows={3}
              value={quote.notizen}
              onChange={(e) => update("notizen", e.target.value)}
              data-testid="input-notizen"
            />
          </div>

          <BillingWorkflowPanel
            quote={quote}
            activeId={activeId}
            onReload={onReloadQuote}
            onOpenInvoice={onOpenGeneratedInvoice}
          />

          <DocumentRelationsPanel
            quote={quote}
            onReload={onReloadQuote}
            onOpenDocument={onOpenGeneratedInvoice}
          />

          <Separator />

          <h3 className="font-semibold text-sm uppercase tracking-wider text-[var(--brand-primary)]">
            Anlagen zum PDF hinzufügen
          </h3>
          {attachments.length === 0 ? (
            <div className="text-xs text-[var(--brand-muted)] rounded p-2 bg-[var(--brand-bg)]">
              Noch keine Dokumente hochgeladen – siehe Einstellungen → Dokumente.
            </div>
          ) : (
            <div className="space-y-2">
              {attachments
                .map((a) => {
                  const checked = (quote.attachments || []).includes(a.id);
                  return (
                    <label
                      key={a.id}
                      className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition ${
                        checked
                          ? "border-[var(--brand-primary)] bg-[var(--brand-bg)]"
                          : "border-[var(--brand-border)] bg-white"
                      }`}
                      data-testid={`attach-checkbox-${a.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleAttachment(a.id)}
                        className="w-4 h-4 accent-[var(--brand-primary)]"
                      />
                      <ShieldCheck className="w-4 h-4 text-[var(--brand-primary)]" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-[var(--brand-ink)] truncate">
                          {a.label}
                        </div>
                        <div className="text-[11px] text-[var(--brand-muted)] truncate">
                          {a.filename}
                        </div>
                      </div>
                    </label>
                  );
                })}
            </div>
          )}
        </div>
      </Card>

      {/* Preview */}
      <div>
        <div className="text-xs text-[var(--brand-muted)] mb-2 uppercase tracking-wider">
          Live-Vorschau · A4
        </div>
        <div
          className="bg-[var(--brand-border)] p-2 sm:p-4 rounded-md shadow-inner overflow-auto"
          data-testid="preview-wrapper"
        >
          {/* Mobile: auto-scale A4 (794px) to fit screen width */}
          <div
            className="origin-top-left mx-auto md:transform-none"
            style={{
              transform: "scale(var(--preview-scale, 1))",
              transformOrigin: "top left",
              width: "794px",
              height: "calc(var(--preview-height-est, 1123px) * var(--preview-scale, 1))",
            }}
          >
            <QuotePreview
              ref={previewRef}
              quote={quote}
              total={total}
              company={company}
              steuer={steuer}
              attachments={attachments}
              signature={signature}
            />
          </div>
        </div>
      </div>
    </div>
  </>
  );
};
